// C:\New_shopworx\shopworx\packages\dashboards\cellwise-downtime-dashboard\src
// \utils\downtime-colors.js

const DEFAULT_DEPARTMENT_COLOR = '#455A64';
const CODE_DEPARTMENT_COLORS = [
  '#1976D2', '#388E3C', '#F57F17', '#7B1FA2', '#D32F2F',
  '#0097A7', '#C2185B', '#AFB42B', '#E64A19', '#455A64',
];
const DEFAULT_SEVERITY_DEPARTMENT_COLORS = {
  dark: ['#D32F2F', '#F57C00', '#FBC02D', '#9CCC65', '#43A047', '#81C784', '#607D8B'],
  light: ['#B71C1C', '#EF6C00', '#F9A825', '#7CB342', '#388E3C', '#66BB6A', '#78909C'],
};

export const normalizeDepartmentCategory = (category) => (
  (category || 'other').toString().toLowerCase().trim().replace(/\s+/g, ' ')
);

const formatDepartmentCategory = (category) => {
  const value = normalizeDepartmentCategory(category);
  if (!value || value === '-' || value === 'no reason' || value === 'other') return 'other';

  if (value.length <= 4) return value.toUpperCase();

  return value.split(' ').map((word) => (
    word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word
  )).join(' ');
};

const getStableHash = (value = '') => {
  let hash = 0;
  const text = value.toString();

  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 2147483647;
  }

  return Math.abs(hash);
};

const getDepartmentPalette = (config = {}, theme = 'light') => {
  const deptColors = config && config.deptColors;
  if (!deptColors || typeof deptColors !== 'object') return [];

  const activeTheme = theme === 'dark' ? 'dark' : 'light';
  const severityPalette = deptColors.severity;
  if (severityPalette && Array.isArray(severityPalette[activeTheme])) {
    const configuredSeverity = severityPalette[activeTheme].filter(Boolean);
    if (configuredSeverity.length) return configuredSeverity;
  }

  const activePalette = Array.isArray(deptColors[activeTheme]) ? deptColors[activeTheme] : [];
  if (activePalette.length) return activePalette.filter(Boolean);

  if (Array.isArray(deptColors.dark)
     && deptColors.dark.length) return deptColors.dark.filter(Boolean);
  if (Array.isArray(deptColors.light)
     && deptColors.light.length) return deptColors.light.filter(Boolean);

  return [];
};

const getSeverityPalette = (config = {}, theme = 'light') => {
  const deptColors = config && config.deptColors;
  const activeTheme = theme === 'dark' ? 'dark' : 'light';

  if (deptColors && deptColors.severity && Array.isArray(deptColors.severity[activeTheme])) {
    const configuredSeverity = deptColors.severity[activeTheme].filter(Boolean);
    if (configuredSeverity.length) return configuredSeverity;
  }

  return DEFAULT_SEVERITY_DEPARTMENT_COLORS[activeTheme];
};

export const getDepartmentColorByIndex = (index, config = {}, theme = 'light') => {
  const safeIndex = Number.isInteger(index) && index >= 0 ? index : 0;
  const severityPalette = getSeverityPalette(config, theme);
  if (severityPalette.length) {
    return severityPalette[safeIndex % severityPalette.length];
  }

  const configPalette = getDepartmentPalette(config, theme);
  if (configPalette[safeIndex]) return configPalette[safeIndex];

  if (CODE_DEPARTMENT_COLORS[safeIndex]) {
    return CODE_DEPARTMENT_COLORS[safeIndex];
  }

  return DEFAULT_DEPARTMENT_COLOR;
};

export const getDowntimeRowValue = (row = {}) => {
  if (!row || typeof row !== 'object') return 0;
  if ('totaldowntimeinmins' in row) return Number(row.totaldowntimeinmins) || 0;
  if ('downtime' in row) return Number(row.downtime) || 0;
  if ('totaldowntime' in row) return Number(row.totaldowntime) || 0;
  if ('totalDowntime' in row) return Number(row.totalDowntime) || 0;
  if ('downtimeinmins' in row) return Number(row.downtimeinmins) || 0;
  if ('downtimeInMins' in row) return Number(row.downtimeInMins) || 0;
  if ('duration' in row) return Number(row.duration) || 0;
  if ('durationinmins' in row) return Number(row.durationinmins) || 0;
  return 0;
};

export const getDowntimeCategoryFromRow = (row = {}, downtimeReasons = [], fallbackCategory = 'other') => {
  const rowReasonText = (row.downtimereason || row.reason || '').toString().toLowerCase().trim();
  const rowCategory = (row.category || '').toString().toLowerCase().trim();
  const rowDepartment = (row.department || '').toString().toLowerCase().trim();

  if (rowReasonText === 'no reason' || rowReasonText === 'none') return 'other';

  const safeReasons = Array.isArray(downtimeReasons) ? downtimeReasons : [];
  const officialReason = safeReasons.find((reason) => {
    if (!reason) return false;
    if (reason.code && row.code && reason.code.toString() === row.code.toString() && row.code !== '-') {
      return true;
    }

    const masterText = (reason.downtimereason || reason.reason || reason.name || '')
      .toLowerCase()
      .trim();
    const masterCategoryText = (reason.category || '').toString().toLowerCase().trim();
    const masterDepartmentText = (reason.department || '').toString().toLowerCase().trim();

    if (rowReasonText && masterText === rowReasonText) return true;
    if (rowCategory && masterCategoryText && rowCategory === masterCategoryText) return true;
    if (rowDepartment && masterDepartmentText
       && rowDepartment === masterDepartmentText) return true;

    return false;
  });

  if (officialReason) {
    if (officialReason.department && officialReason.department !== '-') {
      return formatDepartmentCategory(officialReason.department);
    }

    if (officialReason.category && officialReason.category !== '-') {
      return formatDepartmentCategory(officialReason.category);
    }
  }

  if (rowDepartment && rowDepartment !== '-') return formatDepartmentCategory(rowDepartment);
  if (rowCategory && rowCategory !== '-') return formatDepartmentCategory(rowCategory);

  return formatDepartmentCategory(fallbackCategory);
};

export const buildDowntimeDepartmentList = ({
  categoryMap = {},
  downtimeReasons = [],
} = {}) => {
  const departments = {};

  const addDepartment = (category) => {
    const normalizedCategory = normalizeDepartmentCategory(category);
    if (!normalizedCategory || normalizedCategory === '-') return;

    if (!departments[normalizedCategory]) {
      departments[normalizedCategory] = formatDepartmentCategory(normalizedCategory);
    }
  };

  const safeReasons = Array.isArray(downtimeReasons) ? downtimeReasons : [];
  safeReasons.forEach((reason) => {
    if (reason.department && reason.department !== '-') addDepartment(reason.department);
    else if (reason.category && reason.category !== '-') addDepartment(reason.category);
  });

  Object.keys(categoryMap || {}).forEach((category) => {
    if (category !== '-') addDepartment(category);
  });

  return Object.values(departments).sort((left, right) => {
    const leftValue = normalizeDepartmentCategory(left);
    const rightValue = normalizeDepartmentCategory(right);
    if (leftValue === 'other') return 1;
    if (rightValue === 'other') return -1;
    return leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base' });
  });
};

export const buildDowntimeDepartmentOrder = (rows = [], downtimeReasons = []) => {
  const safeReasons = Array.isArray(downtimeReasons) ? downtimeReasons : [];
  const safeRows = Array.isArray(rows) ? rows : [];
  const orderMap = {};

  const addCategory = (category) => {
    const normalizedCategory = normalizeDepartmentCategory(category);
    if (!normalizedCategory || normalizedCategory === '-' || normalizedCategory === 'other') return;

    if (!orderMap[normalizedCategory]) {
      orderMap[normalizedCategory] = formatDepartmentCategory(normalizedCategory);
    }
  };

  safeReasons.forEach((reason) => {
    if (reason.department && reason.department !== '-') addCategory(reason.department);
    else if (reason.category && reason.category !== '-') addCategory(reason.category);
  });

  safeRows.forEach((row) => {
    addCategory(getDowntimeCategoryFromRow(row, safeReasons, 'other'));
  });

  const orderedDepartments = Object.values(orderMap);
  orderedDepartments.push('Other');

  return orderedDepartments;
};

export const buildDowntimeDepartmentDisplayOrder = (rows = [], downtimeReasons = [], limit = 6) => {
  const safeReasons = Array.isArray(downtimeReasons) ? downtimeReasons : [];
  const safeRows = Array.isArray(rows) ? rows : [];
  const orderMap = {};

  const addCategory = (category) => {
    const normalizedCategory = normalizeDepartmentCategory(category);
    if (!normalizedCategory || normalizedCategory === '-' || normalizedCategory === 'other') return;

    if (!orderMap[normalizedCategory]) {
      orderMap[normalizedCategory] = formatDepartmentCategory(normalizedCategory);
    }
  };

  safeReasons.forEach((reason) => {
    if (reason.department && reason.department !== '-') addCategory(reason.department);
    else if (reason.category && reason.category !== '-') addCategory(reason.category);
  });

  safeRows.forEach((row) => {
    addCategory(getDowntimeCategoryFromRow(row, safeReasons, 'other'));
  });

  const orderedDepartments = Object.values(orderMap).slice(0, limit);
  orderedDepartments.push('Other');

  return orderedDepartments;
};

const mergeDepartmentValue = (total, value) => {
  if (typeof value === 'number') return (Number(total) || 0) + value;
  if (!value || typeof value !== 'object') return total;

  const mergedValue = total && typeof total === 'object' ? { ...total } : {};
  Object.keys(value).forEach((nestedKey) => {
    mergedValue[nestedKey] = (Number(mergedValue[nestedKey])
     || 0) + (Number(value[nestedKey]) || 0);
  });
  return mergedValue;
};

export const getDowntimeCategoryColor = (
  config = {},
  categoryOrIndex = null,
  theme = 'light',
  departmentOrder = [],
) => {
  if (Number.isInteger(categoryOrIndex)) {
    return getDepartmentColorByIndex(categoryOrIndex, config, theme);
  }

  if (typeof categoryOrIndex === 'string' && categoryOrIndex.trim()) {
    const normalizedCategory = normalizeDepartmentCategory(categoryOrIndex);
    const normalizedOrder = Array.isArray(departmentOrder)
      ? departmentOrder.map((item) => normalizeDepartmentCategory(item))
      : [];
    const departmentIndex = normalizedOrder.indexOf(normalizedCategory);

    if (departmentIndex >= 0) {
      return getDepartmentColorByIndex(departmentIndex, config, theme);
    }

    return getDepartmentColorByIndex(getStableHash(normalizedCategory), config, theme);
  }

  return DEFAULT_DEPARTMENT_COLOR;
};

export const summarizeDowntimeByDepartment = (rows = [], downtimeReasons = [], limit = 6) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeReasons = Array.isArray(downtimeReasons) ? downtimeReasons : [];
  const totals = {};
  let otherTotal = 0;

  safeRows.forEach((row) => {
    const category = getDowntimeCategoryFromRow(row, safeReasons, 'other');
    const value = getDowntimeRowValue(row);
    const normalizedCategory = normalizeDepartmentCategory(category);
    if (normalizedCategory === 'other') {
      otherTotal += (Number(value) || 0);
      return;
    }

    totals[normalizedCategory] = (Number(totals[normalizedCategory]) || 0) + (Number(value) || 0);
  });

  const visibleDepartments = buildDowntimeDepartmentDisplayOrder(safeRows, safeReasons, limit)
    .filter((department) => normalizeDepartmentCategory(department) !== 'other');
  const visibleDepartmentSet = new Set(
    visibleDepartments.map((department) => normalizeDepartmentCategory(department)),
  );

  const summary = visibleDepartments.map((department) => {
    const normalizedDepartment = normalizeDepartmentCategory(department);
    return {
      name: department,
      total: Number(totals[normalizedDepartment]) || 0,
    };
  });

  const remainingTotal = Object.entries(totals).reduce((sum, [name, total]) => (
    visibleDepartmentSet.has(normalizeDepartmentCategory(name))
      ? sum
      : sum + (Number(total) || 0)
  ), 0);

  summary.push({
    name: 'Other',
    total: otherTotal + remainingTotal,
    isOther: true,
  });

  return summary;
};

export const getDowntimeDepartmentValue = (categoryMap = {}, category) => {
  const normalizedCategory = normalizeDepartmentCategory(category);
  return Object.keys(categoryMap || {}).reduce((total, key) => {
    if (normalizeDepartmentCategory(key) !== normalizedCategory) return total;
    return mergeDepartmentValue(total, categoryMap[key]);
  }, 0);
};

export const summarizeDowntimeByMachineCategory = (rows = [], downtimeReasons = []) => {
  const safeReasons = Array.isArray(downtimeReasons) ? downtimeReasons : [];
  const machineSet = new Set();
  const deptMap = {};

  rows.forEach((row) => {
    const mName = row.machinename || row.machine || row.assetname || 'Unknown';
    const value = getDowntimeRowValue(row);
    const category = getDowntimeCategoryFromRow(row, safeReasons, 'other');

    machineSet.add(mName);
    if (!deptMap[category]) deptMap[category] = {};
    deptMap[category][mName] = (Number(deptMap[category][mName]) || 0) + value;
  });

  return {
    categories: Array.from(machineSet).sort(),
    deptMap,
  };
};
